// ============================================================================
// ⚠️ DEPRECATED FILE - DO NOT USE FOR NEW DEVELOPMENT
// ============================================================================
//
// This file is DEPRECATED as of 2026-02-02.
// New location: src/pre-deployment/src/components/TrackingService/
//
// ============================================================================
// WHAT CHANGED
// ============================================================================
// The unified tracking system has been migrated to a component folder structure
// following semantic naming conventions. All tracking services are now located
// in the TrackingService component folder with:
//   - Clear service/hook/utility separation
//   - ErrorHandling integration (RawErrorService, ErrorLoggingService)
//   - Full CRUD operations via TrackingCRUDService
//   - React hooks for reactive state management
//
// ============================================================================
// WHY IT CHANGED
// ============================================================================
// 1. Follow semantic naming conventions (PascalCase services, camelCase hooks)
// 2. Better organization with services/, hooks/, utils/ subdirectories
// 3. Integration with ErrorHandling system (RawErrorService)
// 4. Improved maintainability and code organization
// 5. Support for CRUD operations on tracking parameters
//
// ============================================================================
// MIGRATION PATH
// ============================================================================
//
// OLD IMPORT (DEPRECATED):
//   import { ChapterProgress, VocabularyTracking } from '../data/tracking.js';
//   import { useChapterProgress, useKnowledgeProfile } from '../data/tracking.js';
//
// NEW IMPORT (USE THIS):
//   // For services:
//   import { TrackingV2Service } from '../components/TrackingService';
//   import { LearningProgressService } from '../components/TrackingService';
//   import { MasteryService } from '../components/TrackingService';
//   import { TrackingCRUDService } from '../components/TrackingService';
//
//   // For hooks:
//   import { useChapterTracking } from '../components/TrackingService';
//   import { useLearningProgress } from '../components/TrackingService';
//   import { useMasteryData } from '../components/TrackingService';
//   import { useKnowledgeProfile } from '../components/TrackingService';
//
// ============================================================================
// WHAT TO DO
// ============================================================================
// 1. Update all imports to use TrackingService/ folder
// 2. Replace ChapterProgress/VocabularyTracking/etc. with TrackingV2Service methods
// 3. Test that tracking functionality works correctly
// 4. DO NOT DELETE THIS FILE until all imports are migrated - requires user approval
//
// ============================================================================
// BACKWARD COMPATIBILITY
// ============================================================================
// This file will remain functional during the migration period.
// All exports continue to work but are considered deprecated.
// Deprecation warnings appear in console when this file is imported.
//
// Last Updated: 2026-02-02
// Migration Status: IN PROGRESS
// ============================================================================

// Imports must be at the top (ESLint import/first rule)
import { doc, getDoc, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { useState, useEffect, useCallback } from 'react';

import { writeRawError } from '../components/ErrorHandling/AutomatedErrorSystem/RawErrorService';
import { db } from '../services/firebase.js';

// Show deprecation warning when this file is imported
console.warn('⚠️ DEPRECATED: Importing from data/tracking.js. Use components/TrackingService/ instead. See file header for migration instructions.');

// @src/pre-deployment/src/data/tracking.js v2.4 [DEPRECATED]
// v2.4: Decommissioned ErrorLoggingService. Uses RawErrorService for silent tracking.
// OPTIMIZED: Chapter-level tracking with silent error handling
// FIXED: Correct Firebase path structure (even segments = documents)

/**
 * Silent error wrapper for tracking operations
 * NEVER throws - tracking failures should not break the app
 * All errors are logged to Firestore via RawErrorService for debugging
 * 
 * @param {Function} fn - Async function to execute
 * @param {string} context - Context description for logging
 * @param {Object} metadata - Additional metadata for error logging
 * @returns {Promise<any|null>} Result or null on error
 */
const silentTrack = async (fn, context = 'tracking', metadata = {}) => {
  try {
    const result = await fn();
    return result;
  } catch (error) {
    console.error(`❌ Error sending data (${context}):`, error);
    console.warn('⚠️ Tracking failed but app continues normally');

    // v2.4: All errors go to RawErrorService now.
    await writeRawError(error, {
      componentName: `LegacyTracking_${context}`,
      operationType: 'tracking_write',
      severity: 'warning',
      ...metadata
    });

    return null;
  }
};

// ============================================================================
// PATH BUILDERS
// ============================================================================

const getChapterPath = (userId, level, chapter) =>
  `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`;

const getChapterRef = (userId, level, chapter) => {
  try {
    return doc(db, getChapterPath(userId, level, chapter));
  } catch (error) {
    console.error('❌ Error creating chapter reference:', error);
    return doc(db, `users/${userId}/level_${level}/chapter_${chapter}/Learn/LearningProgress`);
  }
};

// ============================================================================
// CHAPTER PROGRESS SERVICE
// ============================================================================

export const ChapterProgress = {
  async initialize(userId, level, chapter) {
    const firestorePath = getChapterPath(userId, level, chapter);
    return silentTrack(async () => {
      if (!userId || !level || !chapter) return false;
      const chapterRef = getChapterRef(userId, level, chapter);
      const snap = await getDoc(chapterRef);

      if (!snap.exists()) {
        await setDoc(chapterRef, {
          level: parseInt(level),
          chapter: parseInt(chapter),
          chapter_unlocked: parseInt(level) === 2 && parseInt(chapter) === 1,
          chapter_started: false,
          chapter_start_date: null,
          last_updated: serverTimestamp(),
          last_completed_module: null,
          chapter_completed: false,
          chapter_completion_date: null,
          modules: {},
          exposures: {},
          attempts: [],
          modules_completed: {},
          sessions: []
        });
      }
      return true;
    }, 'initialize_chapter', { userId, level, chapter, firestorePath });
  },

  async markStarted(userId, level, chapter) {
    return silentTrack(async () => {
      if (!userId || !level || !chapter) return false;
      const chapterRef = getChapterRef(userId, level, chapter);
      await updateDoc(chapterRef, {
        chapter_started: true,
        chapter_start_date: serverTimestamp(),
        last_updated: serverTimestamp()
      });
      return true;
    }, 'mark_chapter_started', { userId, level, chapter });
  },

  async markCompleted(userId, level, chapter) {
    return silentTrack(async () => {
      if (!userId || !level || !chapter) return false;
      const chapterRef = getChapterRef(userId, level, chapter);
      await updateDoc(chapterRef, {
        chapter_completed: true,
        chapter_completion_date: serverTimestamp(),
        last_updated: serverTimestamp()
      });
      if (parseInt(chapter) < 10) {
        await this.unlock(userId, level, parseInt(chapter) + 1);
      }
      return true;
    }, 'mark_chapter_completed', { userId, level, chapter });
  },

  async unlock(userId, level, chapter) {
    return silentTrack(async () => {
      await this.initialize(userId, level, chapter);
      const chapterRef = getChapterRef(userId, level, chapter);
      await updateDoc(chapterRef, {
        chapter_unlocked: true,
        last_updated: serverTimestamp()
      });
    }, 'unlock_chapter', { userId, level, chapter });
  },

  async updateModule(userId, level, chapter, moduleKey, progressData) {
    return silentTrack(async () => {
      if (!userId || !level || !chapter || !moduleKey) return false;
      const chapterRef = getChapterRef(userId, level, chapter);
      const snap = await getDoc(chapterRef);

      if (!snap.exists()) {
        await this.initialize(userId, level, chapter);
      }

      const updates = {
        [`modules.${moduleKey}`]: {
          ...(snap.data()?.modules?.[moduleKey] || {}),
          ...progressData,
          last_opened: progressData.last_opened || serverTimestamp()
        },
        last_updated: serverTimestamp()
      };

      if (progressData.completion) {
        updates.last_completed_module = moduleKey;
        updates[`modules_completed.${moduleKey}`] = progressData.completion_time || serverTimestamp();
      }

      await updateDoc(chapterRef, updates);
      return true;
    }, `update_module_${moduleKey}`, { userId, level, chapter, moduleKey });
  },

  async addQuestion(userId, level, chapter, moduleKey, questionIndex, questionData) {
    return silentTrack(async () => {
      if (!userId || !level || !chapter || !moduleKey || questionIndex === undefined) return false;
      const chapterRef = getChapterRef(userId, level, chapter);
      const updates = {
        [`modules.${moduleKey}.questions.${questionIndex}`]: {
          ...questionData,
          timestamp: serverTimestamp()
        },
        [`modules.${moduleKey}.last_question_answered`]: questionIndex,
        last_updated: serverTimestamp()
      };

      await updateDoc(chapterRef, updates);
      return true;
    }, `add_question_${moduleKey}`, { userId, level, chapter, moduleKey, questionIndex });
  },

  async getData(userId, level, chapter) {
    return silentTrack(async () => {
      if (!userId || !level || !chapter) return null;
      const chapterRef = getChapterRef(userId, level, chapter);
      const snap = await getDoc(chapterRef);
      return snap.exists() ? snap.data() : null;
    }, 'get_chapter_data', { userId, level, chapter });
  }
};

// ============================================================================
// EXPOSURE TRACKING
// ============================================================================

export const ExposureTracking = {
  async markExposed(userId, level, chapter, contentId, contentType) {
    return silentTrack(async () => {
      const chapterRef = getChapterRef(userId, level, chapter);
      const snap = await getDoc(chapterRef);
      if (!snap.data()?.exposures?.[contentId]) {
        await updateDoc(chapterRef, {
          [`exposures.${contentId}`]: new Date().toISOString(),
          last_updated: serverTimestamp()
        });
      }
    }, `mark_exposed_${contentId}`, { userId, level, chapter, contentId, contentType });
  },

  async markAllVocabExposed(userId, level, chapter, vocabIds) {
    return silentTrack(async () => {
      const chapterRef = getChapterRef(userId, level, chapter);
      const timestamp = new Date().toISOString();
      const exposures = {};
      vocabIds.forEach(id => {
        exposures[`exposures.${id}`] = timestamp;
      });
      exposures.last_updated = serverTimestamp();
      await updateDoc(chapterRef, exposures);
    }, 'mark_all_vocab_exposed', { userId, level, chapter, vocabCount: vocabIds.length });
  }
};

// ============================================================================
// ATTEMPT TRACKING
// ============================================================================

export const AttemptTracking = {
  async addAttempt(userId, level, chapter, attemptData) {
    return silentTrack(async () => {
      const chapterRef = getChapterRef(userId, level, chapter);
      const attempt = {
        event_id: `attempt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        ...attemptData
      };
      await updateDoc(chapterRef, {
        attempts: arrayUnion(attempt),
        last_updated: serverTimestamp()
      });
    }, `add_attempt_${attemptData.content_id}`, { userId, level, chapter, contentId: attemptData.content_id });
  },

  async addVocabAttempt(userId, level, chapter, wordId, moduleKey, data) {
    return this.addAttempt(userId, level, chapter, {
      content_type: 'vocabulary',
      content_id: wordId,
      module: moduleKey,
      exercise_type: data.exercise_type || 'translation',
      time_taken: data.time_taken || 0,
      attempt_number: data.attempt_number || 1,
      initial_exposure: data.initial_exposure || false,
      correct: data.correct,
      user_answer: data.user_answer,
      interaction_pattern: data.interaction_pattern || 'multiple_choice'
    });
  },

  async addGrammarAttempt(userId, level, chapter, grammarId, moduleKey, data) {
    return this.addAttempt(userId, level, chapter, {
      content_type: 'grammar',
      content_id: grammarId,
      module: moduleKey,
      exercise_type: data.exercise_type || 'fill_blank',
      time_taken: data.time_taken || 0,
      attempt_number: data.attempt_number || 1,
      initial_exposure: data.initial_exposure || false,
      correct: data.correct,
      user_answer: data.user_answer,
      confidence: data.confidence || 0.5,
      difficulty: data.difficulty || 'medium',
      interaction_pattern: data.interaction_pattern || 'constructed_response'
    });
  }
};

// ============================================================================
// CONTENT-SPECIFIC WRAPPERS (Backward Compatibility)
// ============================================================================

export const VocabularyTracking = {
  async markExposed(userId, level, chapter, sessionType, wordId) {
    return ExposureTracking.markExposed(userId, level, chapter, wordId, 'vocabulary');
  },
  async updateMastery(userId, level, chapter, sessionType, wordId, attemptData) {
    return AttemptTracking.addVocabAttempt(userId, level, chapter, wordId, sessionType, {
      correct: attemptData.is_correct,
      exercise_type: attemptData.test_type,
      user_answer: attemptData.english,
      interaction_pattern: attemptData.test_type === 'spelling' ? 'direct_input' : 'multiple_choice'
    });
  }
};

export const GrammarTracking = {
  async markExposed(userId, level, chapter, sessionType, grammarId) {
    return ExposureTracking.markExposed(userId, level, chapter, grammarId, 'grammar');
  },
  async updateMastery(userId, level, chapter, sessionType, grammarId, attemptData) {
    return AttemptTracking.addGrammarAttempt(userId, level, chapter, grammarId, sessionType, {
      correct: attemptData.is_correct,
      exercise_type: 'grammar_exercise',
      user_answer: attemptData.grammar_title || 'N/A'
    });
  }
};

export const DialogueTracking = {
  async markExposed(userId, level, chapter, sessionType, dialogueId) {
    return ExposureTracking.markExposed(userId, level, chapter, dialogueId, 'dialogue');
  },
  async updateMastery(userId, level, chapter, sessionType, dialogueId, attemptData) {
    return AttemptTracking.addAttempt(userId, level, chapter, {
      content_type: 'dialogue',
      content_id: dialogueId,
      module: sessionType,
      exercise_type: 'comprehension',
      correct: attemptData.is_correct,
      user_answer: 'dialogue_response',
      scene_number: attemptData.scene_number,
      comprehension_score: attemptData.comprehension_score
    });
  }
};

// ============================================================================
// REACT HOOKS
// ============================================================================

export const useChapterProgress = (userId, level, chapter) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId || !level || !chapter) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const chapterData = await ChapterProgress.getData(userId, level, chapter);
      setData(chapterData);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId, level, chapter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
};

export const useKnowledgeProfile = (userId, level, chapter, _sessionType = 'Learn') => {
  const { data: chapterData, loading, error } = useChapterProgress(userId, level, chapter);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!chapterData) return;
    const profile = {
      vocabulary: { exposed_only: [], has_incorrect: [], all_correct: [] },
      grammar: { exposed_only: [], has_incorrect: [], all_correct: [] },
      dialogue: { exposed_only: [], has_incorrect: [], all_correct: [] }
    };

    const attemptsByContent = {};
    (chapterData.attempts || []).forEach(attempt => {
      const key = attempt.content_id;
      if (!attemptsByContent[key]) {
        attemptsByContent[key] = {
          type: attempt.content_type,
          hasIncorrect: false,
          allCorrect: true,
          totalAttempts: 0
        };
      }
      attemptsByContent[key].totalAttempts++;
      if (!attempt.correct) {
        attemptsByContent[key].hasIncorrect = true;
        attemptsByContent[key].allCorrect = false;
      }
    });

    Object.entries(attemptsByContent).forEach(([contentId, stats]) => {
      const type = stats.type;
      if (stats.hasIncorrect) {
        profile[type]?.has_incorrect.push(contentId);
      } else if (stats.allCorrect && stats.totalAttempts > 0) {
        profile[type]?.all_correct.push(contentId);
      }
    });

    Object.keys(chapterData.exposures || {}).forEach(contentId => {
      if (!attemptsByContent[contentId]) {
        if (contentId.includes('gmr')) {
          profile.grammar.exposed_only.push(contentId);
        } else if (contentId.includes('dialogue')) {
          profile.dialogue.exposed_only.push(contentId);
        } else {
          profile.vocabulary.exposed_only.push(contentId);
        }
      }
    });
    setProfile(profile);
  }, [chapterData]);

  return { profile, loading, error };
};

export const useMasteryData = useChapterProgress;
export const useExposureData = useChapterProgress;

export const Tracking = {
  ...ChapterProgress,
  ...ExposureTracking,
  ...AttemptTracking
};

export default Tracking;
